// Angular
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  OnInit,
  Renderer2,
  ViewChild,
} from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { Router } from '@angular/router';

// Ionic
import {
  IonButton,
  IonContent,
  IonIcon,
  IonSelect,
  IonSelectOption,
  ModalController,
  AlertController
} from '@ionic/angular/standalone';

// Third party
import * as _ from 'lodash';
import moment from 'moment';

// Local library
import { AuthFacade, ILogin } from '@amwager/authentication/data-access';
import {
  ConvertToRangePipe,
  CountdownPipe,
  GetCurrentRacePipe,
  GetEventStatusPipe,
  GetRaceStatusPipe,
  GetTrackTypePipe,
  HorseWeightPipe,
  InactivityService,
  LegHeightDirective,
  OrdinalsPipe,
  RaceStringToRangePipe,
  SanitizerPipe,
  SortEventsPipe,
  UnsubscribeObservableService
} from '@amwager/util';
import { IEventTrackRaceState, ISelectedRace, WagerFacade } from '@amwager/wager/data-access';
import {
  convertTimeToMinutes,
  GUEST,
  IAccountPreferencesModel,
  IGetEventsDetailsResponse,
  IRace,
  SERVER_ERROR_MESSAGE
} from '@amwager/util-ts';
import {
  CommonFacade,
  PermissionService,
  SessionService
} from '@amwager/data-access';

// Local file
import { PlayVideoComponent } from '../play-video/play-video.component';
import { RaceComponent } from '../race/race.component';
import { Dictionary, EntityState } from '@ngrx/entity';
import { EventTrackListComponent } from '../event-track-list/event-track-list.component';
import { Subscription, distinctUntilChanged, interval, take, takeUntil, timeInterval } from 'rxjs';
import { VideoComponent } from '@amwager/feature';
import { AccountFacade } from '@amwager/account/data-access';
import { MatrixComponent } from '../matrix/matrix.component';
import { TicketFacade } from '@amwager/ticket/data-access';


@Component({
  selector: 'feature-ion-wager',
  standalone: true,
  imports: [
    CommonModule,
    IonContent,
    IonButton,
    IonSelect,
    IonSelectOption,
    IonIcon,
    RaceStringToRangePipe,
    GetCurrentRacePipe,
    GetRaceStatusPipe,
    SortEventsPipe,
    GetEventStatusPipe,
    GetTrackTypePipe,
    ConvertToRangePipe,
    LegHeightDirective,
    OrdinalsPipe,
    HorseWeightPipe,
    RaceComponent,
    CountdownPipe,
    VideoComponent,
    SanitizerPipe,
    MatrixComponent
  ],
  providers: [DatePipe, GetEventStatusPipe, SortEventsPipe],
  templateUrl: './wager.component.html',
  styleUrl: './wager.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class WagerComponent extends
UnsubscribeObservableService implements OnInit  {
  @ViewChild('eventSelection') eventSelection?: IonSelect;

  SERVER_ERROR_MESSAGE = SERVER_ERROR_MESSAGE;

  userFullName$: string | undefined;

  playLiveVideo: boolean = false;
  // triggerLiveVideo: boolean = false;
  currentRaceStatus!: number;

  events!: IGetEventsDetailsResponse[];
  selectedEvent!: IGetEventsDetailsResponse;
  prevSelectedEvent?: IGetEventsDetailsResponse;
  selectedModifier!: string;
  sortBy: string = 'MTP';
  eventTypeFilter: string[] = [
    'Thoroughbred',
    'Quarterhorse',
    'Greyhound',
    'Harness',
    'International'
  ];
  triggerFromPreference: boolean = false;
  triggerFromBetResult: boolean = false;
  triggerAutoUpdateEvents: boolean = false;
  latestFinishedEvent!: IGetEventsDetailsResponse;
  emptyEvent!: IGetEventsDetailsResponse;

  isEventTrackRaceRequestingAPI: boolean = true;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  loadingModal: any;

  setListFavorites!: Set<string>;

  isSystemError: boolean = false;

  private firstOnline = true;

  private modal: any;
  private triggerMatrixModal_subs?: Subscription;
  private getIsConnectedInternetSubs?: Subscription;

  private triggerMatrixModal?: string;

  private showAlertInfoHandler?: HTMLIonAlertElement;
  private showAlertErrorHandler?: HTMLIonAlertElement;

  selectedRace?: ISelectedRace;


  constructor(
    private readonly authFacade: AuthFacade,
    private readonly accountFacade: AccountFacade,
    private readonly cdRef: ChangeDetectorRef,
    private readonly wagerFacade: WagerFacade,
    private readonly commonFacade: CommonFacade,
    private readonly ticketFacade: TicketFacade,
    private readonly elementRef: ElementRef,
    private readonly permissionService: PermissionService,
    private readonly sessionService: SessionService,
    private readonly router: Router,
    private readonly modalCtrl: ModalController,
    private readonly alertCtrl: AlertController,
    private readonly getEventStatusPipe: GetEventStatusPipe,
    private readonly sortEventsPipe: SortEventsPipe,
    private readonly renderer: Renderer2,
    private readonly inactivityService: InactivityService,
  ) {
    super();

    this.authFacade.isLogoutSuccess$().subscribe((isLogout?: boolean) => {
      if (isLogout) {
        (this.eventSelection as any)['value'] = null; // Reset value (optional)
        (document.querySelector('.select-popover') as any)?.dismiss(); // Close the alert overlay if open

        this.modalCtrl?.dismiss();
        this.modal?.dismiss();

        this.alertCtrl?.dismiss();
        this.showAlertInfoHandler?.dismiss();
        this.showAlertErrorHandler?.dismiss();

        this.cdRef.markForCheck(); // re-render for template
      }
    });

    this.authFacade.login$().subscribe((loginData?: ILogin) => {
      this.isSystemError = !this.sessionService.hasToken() ? true : false;
    });

    // support if no token from cookies after 3 seconds of initialization
    setTimeout(() => {
      this.authFacade.login$()
        .pipe(take(1))
        .subscribe((loginData?: ILogin) => {
          if (!this.sessionService.hasToken()) this.authFacade.loginGuest();
        });
    }, 3000);

    this.getIsConnectedInternetSubs = this.commonFacade.getIsConnectedInternet$()
      .subscribe((isConnectedInternet) => {

        if (isConnectedInternet?.isConnected) {
          console.log('You are online');

          if (this.firstOnline) {
            this.firstOnline = false;
            return;
          }

          setTimeout(() => {
            this.authFacade.login$()
              .pipe(take(1))
              .subscribe((loginData?: ILogin) => {
                if (!this.sessionService.hasToken()) this.authFacade.loginGuest();
                else {
                  // init states
                  this.wagerFacade.getSelectEventTrackRaceState$()
                    .pipe(take(1))
                    .subscribe((eventTrackRaceState: IEventTrackRaceState) => {
                      if (_.isEmpty(eventTrackRaceState)) {
                        // init state managements
                        this.commonFacade.initState('AppComponent');
                        this.authFacade.initState('AppComponent');
                        this.wagerFacade.initState('AppComponent');
                        this.ticketFacade.initState('AppComponent');
                        this.accountFacade.initState('AppComponent');

                        this.wagerFacade.getSelectAllEventTrackRace$()
                          .pipe(take(1))
                          .subscribe((allEventTrackRace: IGetEventsDetailsResponse[]) => {
                            if (_.isEmpty(allEventTrackRace)) this.wagerFacade.getEventsDetails("WagerComponent:constructor");
                          });
                      }
                      else {
                        this.wagerFacade.getSelectAllEventTrackRace$()
                          .pipe(take(1))
                          .subscribe((allEventTrackRace: IGetEventsDetailsResponse[]) => {
                            if (_.isEmpty(allEventTrackRace)) this.wagerFacade.getEventsDetails("WagerComponent:constructor");
                            else {
                              if (
                                this.selectedRace?.RaceId
                                && this.selectedRace?.runId
                                && this.selectedRace?.eventId
                              ) {
                                this.wagerFacade.getRace(
                                  'WagerComponent:constructor',
                                  this.selectedRace?.RaceId,
                                  this.selectedRace?.runId,
                                  this.selectedRace?.eventId
                                );
                              }
                            }
                          });
                      }
                    });

                }
              });
          }, 500);
        }
      });

    // update events every 25000
    interval(25000)
      .pipe(
        distinctUntilChanged() // Suppress consecutive duplicates
      )
      .subscribe((value) => {
        // console.log('interval:', value)

        this.isSystemError = !this.sessionService.hasToken() ? true : false;

        this.wagerFacade.toUpdateGeneralEvents('WagerComponent:constructor');

        // don't exit if video is playing
        if (document.querySelector("iframe.live-video") || document.querySelector("video")) {
          this.inactivityService.resetInactivityTimer('WagerComponent:constructor');
        }

        if (!navigator.onLine) return;

        if (!this.sessionService.hasToken()) this.authFacade.loginGuest();
        else {
          // init states
          this.wagerFacade.getSelectEventTrackRaceState$()
            .pipe(take(1))
            .subscribe((eventTrackRaceState: IEventTrackRaceState) => {
              if (_.isEmpty(eventTrackRaceState)) {
                // init state managements
                this.commonFacade.initState('AppComponent');
                this.authFacade.initState('AppComponent');
                this.wagerFacade.initState('AppComponent');
                this.ticketFacade.initState('AppComponent');
                this.accountFacade.initState('AppComponent');

                this.wagerFacade.getSelectAllEventTrackRace$()
                  .pipe(take(1))
                  .subscribe((allEventTrackRace: IGetEventsDetailsResponse[]) => {
                    if (_.isEmpty(allEventTrackRace)) this.wagerFacade.getEventsDetails("WagerComponent:constructor");
                  });
              }
              else {
                // get event details if no data from state store
                this.wagerFacade.getSelectAllEventTrackRace$()
                  .pipe(take(1))
                  .subscribe((allEventTrackRace: IGetEventsDetailsResponse[]) => {
                    if (_.isEmpty(allEventTrackRace)) this.wagerFacade.getEventsDetails("WagerComponent:constructor");
                  });
              }
            });
        }
      });

    // auto select current race if coming from login page
    this.commonFacade.getBackNavigation$()
      .pipe(
        distinctUntilChanged() // Suppress consecutive duplicates
      )
      .subscribe(async (source) => {
        if (source.includes('login')) {

          if (_.isEmpty(this.events)) return;

          this.prevSelectedEvent = undefined;

          let isFinalAllEvent: boolean[] = [];
          _.range(this.events.length).forEach((count) => {
            isFinalAllEvent.push(
              ["Finished", "Closed"].includes(this.events[count]?.ToteResponse?.EventDetail?.EventStatus)
                ? true
                : false
            )
          });

          if (isFinalAllEvent.every(value => value === true)) {
            const alert = await this.alertCtrl.create({
              header: 'Info',
              message: "All events are in Final status. Please login or signup to see more content of AmWager.",
              buttons: [
                {
                  text: 'Login',
                  handler: () => {
                    this.router.navigate(['login']);
                  }
                }
              ],
              backdropDismiss: false
            });

            await alert.present();

            return;
          }

          if (["Finished", "Closed"].includes(this.selectedEvent?.ToteResponse?.EventDetail?.EventStatus)) {
            this.selectedEvent = this.events[0];
            this.wagerFacade.selectedEventTrack('WagerComponent:constructor', this.selectedEvent);
          }

          this.wagerFacade.selectedRace(
            'WagerComponent:constructor',
            {
              ...this.selectedEvent?.ToteResponse?.EventDetail?.currentRace as any,
              runId: this.selectedEvent?.ToteResponse?.RunId,
              eventId: this.selectedEvent?.ToteResponse?.EventDetail.EventId
            }
          );

          this.cdRef.markForCheck();
        }
      });

    if (this.triggerMatrixModal_subs) {
      this.triggerMatrixModal_subs?.unsubscribe();
      this.triggerMatrixModal_subs = undefined;
    }

    this.triggerMatrixModal_subs = this.wagerFacade.triggerMatrixModal$()
      .pipe(
        distinctUntilChanged() // Suppress consecutive duplicates
      )
      .subscribe(async (trigger) => {

        if (this.triggerMatrixModal === trigger) {
          this.triggerMatrixModal_subs?.unsubscribe();
          this.triggerMatrixModal_subs = undefined;
          return;
        }

        this.triggerMatrixModal = trigger;

        if (!trigger) return;

        if (this.modal) {
          this.modal?.dismiss();
          this.modal = undefined;
          return;
        }

        (document.querySelector('.show-modal') as any)?.dismiss(); // Close the modal overlay if open

        this.modal = await this.modalCtrl.create({
          component: MatrixComponent,
          cssClass: 'matrix',
          backdropDismiss: false
        });
        this.modal.present();

        this.modal.onDidDismiss().then(() => {
          this.modal = undefined;
        });
      });

    // TODO
    this.wagerFacade.toUpdateGeneralEvents$()
      .pipe(
        distinctUntilChanged() // Suppress consecutive duplicates
      )
      .subscribe((toUpdate) => {
        if (!toUpdate) return;

        // re-render data list
        this.wagerFacade.getSelectAllEventTrackRace$()
          .pipe(take(1))
          .subscribe((allEventTrackRace: IGetEventsDetailsResponse[]) => {

            if (_.isEmpty(allEventTrackRace)) return;

            this.triggerAutoUpdateEvents = false;
            this.events = allEventTrackRace as any;
            this.onSelectSortEvents(this.events);

            this.cdRef.detectChanges(); // re-render for template
          });
      });


    this.wagerFacade.getSelectAllEventTrackRace$()
      .subscribe((allEventTrackRace: IGetEventsDetailsResponse[]) => {

        this.triggerAutoUpdateEvents = true;

        // console.log('wager:allEventTrackRace:', allEventTrackRace)
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        this.events = allEventTrackRace as any;
        this.onSelectSortEvents(this.events);

        this.cdRef.markForCheck(); // re-render for template

        // console.log('wager:allEventTrackRace:selectedEvent:', this.selectedEvent)

        if (!this.selectedEvent) {
          // this.onSelectSortEvents(this.events);
          this.selectedEvent = this.events[0];
          this.prevSelectedEvent = this.events[0];
          this.wagerFacade.selectedEventTrack('WagerComponent:constructor', this.selectedEvent);

          // const currentRace = _.find(
          //   this.selectedEvent?.ToteResponse?.EventDetail?.Races, race => _.has(race, 'Current')
          // );

          this.wagerFacade.selectedRace(
            'WagerComponent:constructor',
            {
              ...this.selectedEvent?.ToteResponse?.EventDetail?.currentRace as any,
              // ...currentRace as any,
              runId: this.selectedEvent?.ToteResponse?.RunId,
              eventId: this.selectedEvent?.ToteResponse?.EventDetail.EventId
            }
          );

          // TODO: don't fetch if race is > 30
          // if (!['Final', 'Finished'].includes(this.selectedEvent?.ToteResponse?.EventDetail?.EventStatus)) {
          //   // get video
          //   this.wagerFacade.getEventRaceVideoLive(
          //     'WagerComponent:constructor',
          //     {
          //       toteEventId: this.selectedEvent?.ToteResponse?.EventDetail?.EventId,
          //       width: 720,
          //       height: 576
          //     },
          //     this.selectedEvent?.VideoType === 'IGB' ? 'IGB' : 'default'
          //   );
          // }

        }

        // initiate to call betslip info
        // do not execute if not logged in to avoid 401 unauthorized error
        // observation (getBetslip) execute multiple times more than 10 times
        if (this.sessionService.getRoleId() !== 10)
          this.getBetSlip(this.selectedEvent?.ToteResponse?.RunId);

        this.cdRef.markForCheck(); // re-render for template
      });

    this.wagerFacade.isEventTrackRaceRequestingAPI$()
      .subscribe((isRequestingAPI: boolean) => {
        this.isEventTrackRaceRequestingAPI = isRequestingAPI;
        this.cdRef.markForCheck(); // re-render for template
      });

    this.wagerFacade.getSelectEventTrackRaceEntities$()
      .subscribe((allEventTrackRace: Dictionary<IGetEventsDetailsResponse>) => {
        if (_.isEmpty(allEventTrackRace)) return;

        // console.log('wager:allEventTrackRace:', allEventTrackRace);
        this.selectedEvent = allEventTrackRace[this.selectedEvent?.id as any] as IGetEventsDetailsResponse;

        // this.showAlertError(this.selectedEvent?.Error);

        this.cdRef.markForCheck();

      });

    // this.observerGetSelectedEventTrack('WagerComponent:constructor');

    // this.wagerFacade.getSelectedEventTrack$()
    //   .subscribe((selectedEventTrack?: IGetEventsDetailsResponse) => {
    //     if (selectedEventTrack !== undefined) {
    //       if (selectedEventTrack?.streamVideo !== undefined) {
    //         this.playLiveVideo = true;
    //         this.triggerLiveVideo = true;
    //       }
    //     }
    //     this.cdRef.markForCheck(); // re-render for template
    //   });

    this.wagerFacade.getEventLiveVideo$()
      .subscribe((callback) => {
        if (callback === undefined || callback?.response === undefined) return;

        this.playLiveVideo = true;
        this.cdRef.markForCheck(); // re-render for template
      });

    this.wagerFacade.getLatestFinishedEvent$()
      .subscribe((callback: IGetEventsDetailsResponse) => {
        if (callback === undefined) return;

        this.wagerFacade.toUpdateGeneralEvents('WagerComponent: clickEventList');
        this.triggerFromBetResult = true;
        this.latestFinishedEvent = callback;

        // this.cdRef.markForCheck();
      });

    this.accountFacade.isGetAccountPreferencesSuccess$()
      .subscribe((callback) => {

        if (callback?.isSuccess === undefined) return;

        const response: IAccountPreferencesModel = callback?.response?.AccountPreferences;
        this.sortBy = response?.SortBy || 'MTP';
        this.eventTypeFilter = response?.EventTypeFilter || [];

        this.wagerFacade.toUpdateGeneralEvents('WagerComponent: clickEventList');
        const betSection = this.elementRef.nativeElement.querySelector('.bet-footer-section');

        if (betSection) {
          this.commonFacade.setIsShowFooter(true);
          this.wagerFacade.setIsShowBetPreview(false);
        }

        this.triggerFromPreference = true;

        this.cdRef.markForCheck();
      });

    // QA testing
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    this.commonFacade.qaTesting$().subscribe((qaTesting: any) => {
      if (!qaTesting) return;

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      this.events = qaTesting?.wagerCustom as any;

      this.selectedEvent = this.events[0];
      this.prevSelectedEvent = this.events[0];
      this.wagerFacade.selectedEventTrack('WagerComponent:constructor', this.selectedEvent);

      this.cdRef.detectChanges(); // re-render for template
    });

    this.wagerFacade.getSelectedRace$().subscribe((selectedRace) => {
      this.selectedRace = selectedRace;
      this.cdRef.markForCheck();
    });

    // set to default event from tap races - footer
    this.wagerFacade.getDefaultEvent$()
      .subscribe((isSetDefault) => {

        if (isSetDefault && this.events.length > 0) {
          this.wagerFacade.setDefaultEvent(false);

          const newEvents = this.sortEventsPipe.transform(
            this.events,
            this.sortBy,
            this.eventTypeFilter,
            this.setListFavorites
          );

          const firstEvent = _.find(newEvents, event =>
            _.get(event, 'ToteResponse.EventDetail.EventStatus') === 'Open' &&
            _.get(event, 'ToteResponse.EventDetail.currentRace.RaceStatus') === 0
          );

          this.selectEvent({target: {value: firstEvent}});
        }

      });

    this.observerPreferenceFavorites();
  }


  ngOnInit(): void {
    // update branch 2

    this.authFacade.getUserFullName$()
      .pipe(takeUntil(this.toUnsubscribe$))
      .subscribe( res => {

        // user logged out
        if (res === undefined) {
          this.sortBy = 'MTP';
          this.eventTypeFilter = [
            'Thoroughbred',
            'Quarterhorse',
            'Greyhound',
            'Harness',
            'International'
          ];
        }

        this.userFullName$ = res;
        this.cdRef.markForCheck(); // re-render for template
      });

    this.getIsConnectedInternetSubs?.unsubscribe();
  }

  /**
   * Observers
   */
  // observerGetSelectedEventTrack(sourceInitiator: string): void {
  //   this.wagerFacade.getSelectedEventTrack$()
  //   .subscribe((selectedEventTrack?: IGetEventsDetailsResponse) => {
  //     if (!selectedEventTrack) return;

  //     // this.wagerFacade.getEventRaceVideo(
  //     //   sourceInitiator,
  //     //   {
  //     //     toteEventId: selectedEventTrack?.ToteResponse?.EventDetail?.EventId,
  //     //     width: 720,
  //     //     height: 576,
  //     //   }
  //     // );
  //   });
  // }

  observerPreferenceFavorites(): void {
    this.accountFacade.getPreferenceFavorites$()
      .subscribe((listFavorites?: string[]) => {
        this.setListFavorites = new Set(listFavorites);
      });
  }

  selectedTrackType(type: number, selected?: string[], countryCode?: string): boolean {

    let result: boolean = false;
    let trackTypeIs: string = '';

    switch(type) {
      // Quarterhorse included based on frontend repo
      case 1:
        trackTypeIs = 'Thoroughbred';
        break;

      case 2:
        trackTypeIs = 'Greyhound';
        break;

      case 3:
        trackTypeIs = 'Quarterhorse';
        break;

      case 4:
        trackTypeIs = 'Harness';
        break;
    }

    // !type &&
    if (
      (countryCode === null || countryCode !== 'US') &&
      !selected?.includes('International')
    ) {
      return result;
    }

    if (
      countryCode !== null &&
      countryCode !== 'US' &&
      selected?.includes('International')
    ) {
      trackTypeIs = 'International';
    }

    if (selected?.includes(trackTypeIs)) {
      result = true;
    }

    return result;
  }

  onSelectSortEvents(allEvents: IGetEventsDetailsResponse[]): void {

    if (!allEvents.length)
      return;

    // const today = moment().utc();
    let x: IGetEventsDetailsResponse[] = [];

    if (this.sortBy === 'MTP') {
      x = _.filter(allEvents, (event: IGetEventsDetailsResponse) =>
        this.selectedTrackType(event?.ToteResponse?.EventDetail?.TrackType, this.eventTypeFilter, event?.CountryCode) === true
      );
    }

    if (this.sortBy === 'Name') {
      x = _.concat(
        _.orderBy(
          // others
          _.filter(allEvents, (event: IGetEventsDetailsResponse) => event?.ToteResponse?.EventDetail?.currentRace?.RaceStatus !== 3 &&
            this.selectedTrackType(event?.ToteResponse?.EventDetail?.TrackType, this.eventTypeFilter, event?.CountryCode) === true
          ),
          ['ToteResponse.EventDetail.EventName'],
          ['asc']
        ),
        _.orderBy(
          // final
          _.filter(allEvents, (event: IGetEventsDetailsResponse) => event?.ToteResponse?.EventDetail?.currentRace?.RaceStatus === 3 &&
            this.selectedTrackType(event?.ToteResponse?.EventDetail?.TrackType, this.eventTypeFilter, event?.CountryCode) === true
          ),
          ['ToteResponse.EventDetail.EventName'],
          ['asc']
        )
      );
    }

    this.events = x;
    this.selectedEvent = this.events[0];

    this.cdRef.detectChanges();

    if (this.prevSelectedEvent !== undefined) {
      this.selectedEvent = this.prevSelectedEvent;
    }

    // for wagering preference change display
    // Thoroughbred, Quarterhorse, Harness, Greyhound, International
    if (this.triggerFromPreference) {
      this.triggerFromPreference = false;
      this.prevSelectedEvent = this.events[0];
      this.selectedEvent = this.events[0];

      // refresh event track, race
      this.wagerFacade.selectedEventTrack('WagerComponent:onSelectSortEvents', this.selectedEvent);

      // const currentRace = _.find(
      //   this.selectedEvent?.ToteResponse?.EventDetail?.Races, race => _.has(race, 'Current')
      // );

      this.wagerFacade.selectedRace(
        'WagerComponent:onSelectSortEvents',
        {
          ...this.selectedEvent?.ToteResponse?.EventDetail?.currentRace as any,
          // ...currentRace as any,
          runId: this.selectedEvent?.ToteResponse?.RunId,
          eventId: this.selectedEvent?.ToteResponse?.EventDetail.EventId
        }
      );
    }

    if (this.triggerFromBetResult) {
      this.triggerFromBetResult = false;
      this.prevSelectedEvent = this.latestFinishedEvent;
      this.selectedEvent = this.latestFinishedEvent;

      // refresh event track, race
      this.wagerFacade.selectedEventTrack('WagerComponent:onSelectSortEvents', this.selectedEvent);

      // const currentRace = _.find(
      //   this.selectedEvent?.ToteResponse?.EventDetail?.Races, race => _.has(race, 'Current')
      // );

      this.wagerFacade.selectedRace(
        'WagerComponent:onSelectSortEvents',
        {
          ...this.selectedEvent?.ToteResponse?.EventDetail?.currentRace as any,
          // ...currentRace as any,
          runId: this.selectedEvent?.ToteResponse?.RunId,
          eventId: this.selectedEvent?.ToteResponse?.EventDetail.EventId
        }
      );

      this.wagerFacade.setLatestFinishedEvent(this.emptyEvent);
    }

    this.wagerFacade.getSelectedEventTrack$().subscribe((callback) => {
      if (callback === undefined) {
        if (
          this.sessionService.getUserEmail() === GUEST
          || this.sessionService.getRoleId() === 9
        ) {

          // refresh event track, race
          this.wagerFacade.selectedEventTrack('WagerComponent:onSelectSortEvents', this.selectedEvent);

          this.wagerFacade.selectedRace(
            'WagerComponent:onSelectSortEvents',
            {
              ...this.selectedEvent?.ToteResponse?.EventDetail?.currentRace as any,
              runId: this.selectedEvent?.ToteResponse?.RunId,
              eventId: this.selectedEvent?.ToteResponse?.EventDetail.EventId
            }
          );
        }
      }
    });

    this.cdRef.markForCheck();
  }

  compareWith(o1: any, o2: any) {
    return o1 && o2 ? o1.id === o2.id : o1 === o2;
  }

  async playVideo(): Promise<void> {
    if (this.selectedRace?.RaceStatus === 3) return;

    if (!navigator.onLine) {
      this.commonFacade.setIsConnectedInternet('WagerComponent:playVideo', false);
    }

    if (!this.hasPermission()) {

      if (this.sessionService.getUserEmail() === GUEST || !this.sessionService.hasToken()) {
        this.router.navigate(['login']);
      }
      else {
        this.showAlertInfo('You are not an Identified User.');
      }

      return;
    }

    if (this.selectedEvent) {
      if (this.currentRaceStatus !== 3) {
        // get video
        this.wagerFacade.getEventRaceVideoLive(
          'WagerComponent:playVideo',
          {
            toteEventId: this.selectedEvent?.ToteResponse?.EventDetail?.EventId,
            width: 720,
            height: 576
          },
          this.selectedEvent?.VideoType === 'IGB' ? 'IGB' : 'default'
        );
      }
    }

    // if (this.selectedEvent) {
    //   // this.playLiveVideo = !this.playLiveVideo;
    //   if (!['Final', 'Finished'].includes(this.selectedEvent?.ToteResponse?.EventDetail?.EventStatus)) {
    //     // get video
    //     this.wagerFacade.getEventRaceVideoLive(
    //       'WagerComponent:constructor',
    //       {
    //         toteEventId: this.selectedEvent?.ToteResponse?.EventDetail?.EventId,
    //         width: 720,
    //         height: 576
    //       },
    //       this.selectedEvent?.VideoType === 'IGB' ? 'IGB' : 'default'
    //     );
    //   }
    // }

    // const modal = await this.modalCtrl.create({
    //   component: PlayVideoComponent,
    //   cssClass: 'play-video'
    // });
    // modal.present();

    // const { role } = await modal.onWillDismiss();
    // if (role) this.playLiveVideo = false;

    // this.cdRef.detectChanges();
  }

  doSetRaceStatus($status: number) {
    this.currentRaceStatus = $status;
  }

  closeVideo($event: any) {
    this.playLiveVideo = !this.playLiveVideo;
    // this.triggerLiveVideo = !this.triggerLiveVideo;
  }

  async openEventTrackList(): Promise<void> {
    if (!this.hasPermission()) return;

    const modal = await this.modalCtrl.create({
      component: EventTrackListComponent,
      cssClass: 'event-track-list'
    });
    modal.present();

    const { role } = await modal.onWillDismiss();
    if (role) this.playLiveVideo = false;

    this.cdRef.detectChanges();
  }

  clickEventList(): void {

    // UI: favorite divider selection option
    setTimeout(() => {
      const ionItemAll = document
        .querySelector('.ion-select-event-track')
        ?.querySelectorAll('ion-item');

      const newEvents = this.sortEventsPipe.transform(
        this.events,
        this.sortBy,
        this.eventTypeFilter,
        this.setListFavorites
      );

      this.renderer.setAttribute(
        ionItemAll?.[_.filter(newEvents, ['isFavorite', true])?.length-1],
        'style',
        'border-bottom-width: medium; border-bottom-style: dashed;'
      );

    }, 300);

    this.wagerFacade.toUpdateGeneralEvents('WagerComponent: clickEventList');

    // re-render data list
    this.wagerFacade.getSelectAllEventTrackRace$()
      .pipe(take(1))
      .subscribe((allEventTrackRace: IGetEventsDetailsResponse[]) => {
        // console.log('selectEvent:allEventTrackRace:clickEventList', this.events)
        this.events = allEventTrackRace;
        this.onSelectSortEvents(this.events);

        this.cdRef.detectChanges();
      });
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  selectEvent(ev: any) {

    this.triggerAutoUpdateEvents = true;
    this.prevSelectedEvent = ev.target.value;

    // clear active legs/round
    this.clearLegRoundSelector();
    // console.log('Current value1:',ev.target.value);
    // console.log('Current value2:',ev);
    // console.log('Current value3:',ev.target);

    this.selectedEvent = ev.target.value;
    this.wagerFacade.selectedEventTrack('WagerComponent:constructor', this.selectedEvent);
    this.selectedModifier = '';
    // console.log('current:', this.getEventStatusPipe.transform(
    //   this.selectedEvent?.ToteResponse?.EventDetail?.EventStatus
    // ));
    // console.log('current:',
    //   this.selectedEvent?.ToteResponse?.EventDetail?.EventStatus
    // );

    // const currentRace = _.find(
    //   this.selectedEvent?.ToteResponse?.EventDetail?.Races, race => _.has(race, 'Current')
    // );

    if (this.selectedEvent?.ToteResponse?.EventDetail?.EventStatus === 'Finished') {
      // console.log('current: Finished:',this.selectedEvent?.ToteResponse?.EventDetail?.Races[0] )
      this.wagerFacade.selectedRace(
        'WagerComponent:selectEvent',
        {
          // ...this.selectedEvent?.ToteResponse?.EventDetail?.Races[0] as any,
          // ...currentRace as any,
          ...this.selectedEvent?.ToteResponse?.EventDetail?.currentRace as any,
          runId: this.selectedEvent?.ToteResponse?.RunId,
          eventId: this.selectedEvent?.ToteResponse?.EventDetail.EventId
        }
      );
    }
    else {
      // console.log('current: Finished:else:',this.selectedEvent?.ToteResponse?.EventDetail?.Races[0] )
      this.wagerFacade.selectedRace(
        'WagerComponent:selectEvent',
        {
          ...this.selectedEvent?.ToteResponse?.EventDetail?.currentRace as any,
          // ...currentRace as any,
          runId: this.selectedEvent?.ToteResponse?.RunId,
          eventId: this.selectedEvent?.ToteResponse?.EventDetail.EventId
        }
      );
    }

    // TODO: don't fetch if race is > 30
    // if (!['Final', 'Finished'].includes(this.selectedEvent?.ToteResponse?.EventDetail?.EventStatus)) {
    //   // get video
    //   this.wagerFacade.getEventRaceVideoLive(
    //     'WagerComponent:selectEvent',
    //     {
    //       toteEventId: this.selectedEvent?.ToteResponse?.EventDetail?.EventId,
    //       width: 720,
    //       height: 576
    //     },
    //     this.selectedEvent?.VideoType === 'IGB' ? 'IGB' : 'default'
    //   );
    // }

    // this.swipeRaceSectionTemplateRef.nativeElement.scrollLeft = 0;
    this.cdRef.markForCheck(); // re-render for template
  }

  hasPermission(): boolean {
    if (this.permissionService.isAllowed()) {
      return true;
    }

    this.router.navigate(['/login']);
    return false;
  }

  clearLegRoundSelector(): void {
    const divLegs = this.elementRef.nativeElement.querySelectorAll('.table-bet [class^="runner"]');
    // const divLegs = this.elementRef.nativeElement.querySelectorAll('.table-bet [class^="leg"]');
    divLegs.forEach((leg: HTMLElement) => {
      if (leg?.classList.contains('active') || leg?.classList.contains('couple-entry'))
        leg?.classList.remove('active');
        leg?.classList.remove('couple-entry');
        leg.innerHTML = '';
    });
  }


  trackByFn(index: number, item: any): number {
    return item.id; // Use the unique ID property for tracking
  }

  getBetSlip(runId: string): void {
    if (!runId) return;

    const statuses = {
      1: 'saved',
      3: 'active',
      6: 'close'
    };

    _.map([1, 3, 6], (status) => {
      this.wagerFacade.getBetsAPI(
        {
          runId,
          ticketStatus: status,
          offset: 0,
          next: 500
        },
        (statuses as any)[status],
        'WagerComponent:getBetSlip'
      );
    });
  }

  async showAlertInfo(message: string): Promise<void> {
    this.showAlertInfoHandler = await this.alertCtrl.create({
      header: 'Info',
      message,
      buttons: [
        {
          text: 'Close',
          handler: () => {
          }
        }
      ]
    });

    await this.showAlertInfoHandler.present();
  }

  async showAlertError(error: string): Promise<void> {
    this.showAlertErrorHandler = await this.alertCtrl.create({
      header: 'Error',
      message: error,
      buttons: [
        {
          text: 'Close',
          handler: () => {
            this.modalCtrl.dismiss();
          }
        }
      ]
    });

    await this.showAlertErrorHandler.present();
  }

  reloadPage(): void {
    window.location.reload();
  }

}
